import { useContext, useEffect, useState } from "react";
import ProjectInfo from "../../models/projectInfo";
import SettingsModel from "../../models/settings";
import ModalWindow from "../common/overlays/ModalWindow";
import User from "../../models/user";
import UserContext from "../../contexts/userContext";
import WorkspaceLayout from "../../models/workspaceLayout";

const Settings: React.FC<{
  settings: SettingsModel;
  project: ProjectInfo;
  onResetLayout: any;
  onSetLayout: any;
  onSetProjectDefaultLayout: any;
  onSetProjectDefaultLocation: any;
}> = ({
  settings,
  project,
  onResetLayout,
  onSetLayout,
  onSetProjectDefaultLayout,
  onSetProjectDefaultLocation,
}) => {
  const user = useContext<User | undefined>(UserContext);

  const [showChainage, setShowChainage] = useState<boolean>(false);
  const [showChainageUnit, setShowChainageUnit] = useState<boolean>(false);
  const [showTableHelperColumns, setShowTableHelperColumns] =
    useState<boolean>(false);
  const [isChainageInMeters, setIsChainageInMeters] = useState<boolean>(false);

  const [enableChartsUnitsOnX, setEnableChartsUnitsOnX] =
    useState<boolean>(true);

  const [showCameraImageUrl, setShowCameraImageUrl] = useState<boolean>(false);

  /* reset layout */

  const [showConfirmResetLayoutWindow, setShowConfirmResetLayoutWindow] =
    useState<Boolean>(false);

  const onResetLayoutClicked = () => {
    setShowConfirmResetLayoutWindow(true);
  };

  const onResetLayoutOk = () => {
    setShowConfirmResetLayoutWindow(false);
    onResetLayout();
  };

  const onResetLayoutCancel = () => {
    setShowConfirmResetLayoutWindow(false);
  };

  /* set project default layout */

  const [
    showConfirmSetProjectDefaultLayout,
    setShowConfirmSetProjectDefaultLayout,
  ] = useState<boolean>(false);

  const onSetProjectDefaultLayoutClicked = () => {
    setShowConfirmSetProjectDefaultLayout(true);
  };

  const onSetProjectDefaultLayoutOk = () => {
    setShowConfirmSetProjectDefaultLayout(false);
    onSetProjectDefaultLayout();
  };

  const onSetProjectDefaultLayoutCancel = () => {
    setShowConfirmSetProjectDefaultLayout(false);
  };

  /* --- */

  /* set project default location */

  const [
    showConfirmSetProjectDefaultLocation,
    setShowConfirmSetProjectDefaulLocation,
  ] = useState<boolean>(false);

  const onSetProjectDefaultLocationClicked = () => {
    setShowConfirmSetProjectDefaulLocation(true);
  };

  const onSetProjectDefaultLocationOk = () => {
    setShowConfirmSetProjectDefaulLocation(false);
    onSetProjectDefaultLocation();
  };

  const onSetProjectDefaultLocationCancel = () => {
    setShowConfirmSetProjectDefaulLocation(false);
  };

  /* --- */

  const onShowChainageChanged = (event: any) => {
    const checked = event.target.checked;
    setShowChainage(checked);

    settings.showChainage = checked;

    if (settings.onShowChainageChanged) {
      settings.onShowChainageChanged(checked);
    }

    onSettingsChanged();
  };

  const onShowChainageUnitChanged = (event: any) => {
    const checked = event.target.checked;
    setShowChainageUnit(checked);

    settings.showChainageUnit = checked;

    if (settings.onShowChainageUnitChanged) {
      settings.onShowChainageUnitChanged(checked);
    }

    onSettingsChanged();
  };

  const onShowTableHelperColumnsChanged = (event: any) => {
    const checked = event.target.checked;
    setShowTableHelperColumns(checked);

    settings.showTableHelperColumns = checked;

    if (settings.onShowTableHelperColumnsChanged) {
      settings.onShowTableHelperColumnsChanged(checked);
    }

    // onSettingsChanged();
  };

  const onIsChainageInMetersChanged = (event: any) => {
    const checked = event.target.checked;
    setIsChainageInMeters(checked);

    settings.isChainageInMeters = checked;

    if (settings.onIsChainageInMetersChanged) {
      settings.onIsChainageInMetersChanged(checked);
    }
  };

  const onEnableChartsUnitsOnXChanged = (event: any) => {
    const checked = event.target.checked;
    setEnableChartsUnitsOnX(checked);

    settings.enableChartsUnitsOnX = checked;

    settings.enableChartsUnitsOnXObservable.notify(checked);
  };

  const onShowCameraImageUrlChanged = (event: any) => {
    const checked = event.target.checked;
    setShowCameraImageUrl(checked);

    settings.showCameraImageUrl = checked;

    settings.showCameraImageUrlObservable.notify(checked);
  };

  const onSettingsChanged = () => {
    if (settings.onSettingsChanged) {
      settings.onSettingsChanged(settings);
    }
  };

  useEffect(() => {
    setShowChainage(settings.showChainage);
    setShowChainageUnit(settings.showChainageUnit);
  }, [settings]);

  const getChainageDisplayName = () => {
    if (project.chainageDisplayName && project.chainageDisplayNameShort) {
      return `${project.chainageDisplayName} (${project.chainageDisplayNameShort})`;
    }

    if (project.chainageDisplayName) {
      return project.chainageDisplayName;
    }

    return "Chainage";
  };

  const onSetMyLayout = () => {
    // Layout: Half Map, Pano, Table, Pavement

    var workspaceLayout: WorkspaceLayout = {
      layout: [
        {
          w: 6,
          h: 4,
          x: 6,
          y: 0,
          i: "camera_Pano",
          moved: false,
          static: false,
        },
        {
          w: 2,
          h: 4,
          x: 10,
          y: 4,
          i: "camera_Pavement",
          moved: false,
          static: false,
        },
        { w: 6, h: 8, x: 0, y: 0, i: "map", moved: false, static: false },
        { w: 4, h: 4, x: 6, y: 4, i: "table", moved: false, static: false },
      ],
      layoutInvisibleItems: [
        {
          w: 7,
          h: 4,
          x: 0,
          y: 8,
          i: "camera_Forward",
          moved: false,
          static: false,
        },
      ],
    };

    onSetLayout(workspaceLayout);
  };

  return (
    <div>
      <div className="dropdown-item-padding">
        <button
          // className="dropdown-item"
          className="btn btn-primary btn-sm"
          type="button"
          onClick={onResetLayoutClicked}
        >
          Reset Layout
        </button>
        {showConfirmResetLayoutWindow && (
          <ModalWindow
            title="Reset Layout to Default"
            onOk={onResetLayoutOk}
            onCancel={onResetLayoutCancel}
          >
            <div>Are you sure you want to reset layout to default?</div>
          </ModalWindow>
        )}
      </div>
      <div className="dropdown-item-padding d-flex">
        <div className="d-flex align-items-center">
          <label className="form-check-label" htmlFor="showChainage">
            {getChainageDisplayName()}
          </label>
          <div className="form-switch form-switch-right-text">
            <input
              className="form-check-input form-switch-right-text"
              type="checkbox"
              checked={showChainage}
              onChange={onShowChainageChanged}
              id="showChainage"
            />
          </div>
        </div>
        <div
          className="d-flex align-items-center"
          style={{ marginLeft: "10px" }}
        >
          <label className="form-check-label" htmlFor="showChainageUnit">
            Unit
          </label>
          <div className="form-switch form-switch-right-text">
            <input
              className="form-check-input form-switch-right-text"
              type="checkbox"
              checked={showChainageUnit}
              onChange={onShowChainageUnitChanged}
              id="showChainageUnit"
            />
          </div>
        </div>
      </div>
      {user?.isPortalAdmin && (
        <>
          <br />
          <div className="dropdown-item-padding">
            <div className="badge bg-secondary mb-1">PORTAL ADMIN</div>
          </div>
          <div className="dropdown-item-padding">
            <div style={{ fontSize: "12px", marginBottom: "-8px" }}>Layout</div>
            <span className="mb-2 me-2">Half Map, Pano, Table, Pav</span>
            <button
              // className="dropdown-item"
              className="btn btn-primary btn-sm"
              type="button"
              onClick={onSetMyLayout}
            >
              Set My Layout
            </button>
          </div>
          <div className="dropdown-item-padding">
            <button
              // className="dropdown-item"
              className="btn btn-primary btn-sm"
              type="button"
              onClick={onSetProjectDefaultLocationClicked}
            >
              Set Project Default Location
            </button>
            {showConfirmSetProjectDefaultLocation && (
              <ModalWindow
                title="Set Project Default Location"
                onOk={onSetProjectDefaultLocationOk}
                onCancel={onSetProjectDefaultLocationCancel}
              >
                <div>
                  Are you sure you want to set the current location as project
                  default location?
                </div>
              </ModalWindow>
            )}
          </div>
          <div className="dropdown-item-padding">
            <button
              // className="dropdown-item"
              className="btn btn-primary btn-sm"
              type="button"
              onClick={onSetProjectDefaultLayoutClicked}
            >
              Set Project Default Layout
            </button>
            {showConfirmSetProjectDefaultLayout && (
              <ModalWindow
                title="Set Project Default Layout"
                onOk={onSetProjectDefaultLayoutOk}
                onCancel={onSetProjectDefaultLayoutCancel}
              >
                <div>
                  Are you sure you want to set the current layout as project
                  default layout?
                </div>
              </ModalWindow>
            )}
          </div>
          <div className="dropdown-item-padding d-flex">
            <div className="d-flex align-items-center">
              <label className="form-check-label" htmlFor="showCameraImageUrl">
                Show Camera Image Url
              </label>
              <div className="form-switch form-switch-right-text">
                <input
                  className="form-check-input form-switch-right-text"
                  type="checkbox"
                  checked={showCameraImageUrl}
                  onChange={onShowCameraImageUrlChanged}
                  id="showCameraImageUrl"
                />
              </div>
            </div>
          </div>
          <div className="dropdown-item-padding d-flex">
            <div className="d-flex align-items-center">
              <label
                className="form-check-label"
                htmlFor="showTableHelperColumns"
              >
                Table Helper Columns
              </label>
              <div className="form-switch form-switch-right-text">
                <input
                  className="form-check-input form-switch-right-text"
                  type="checkbox"
                  checked={showTableHelperColumns}
                  onChange={onShowTableHelperColumnsChanged}
                  id="showTableHelperColumns"
                />
              </div>
            </div>
          </div>
          <div className="dropdown-item-padding d-flex">
            <div className="d-flex align-items-center">
              <label className="form-check-label" htmlFor="isChainageInMeters">
                Chainage in Meters
              </label>
              <div className="form-switch form-switch-right-text">
                <input
                  className="form-check-input form-switch-right-text"
                  type="checkbox"
                  checked={isChainageInMeters}
                  onChange={onIsChainageInMetersChanged}
                  id="isChainageInMeters"
                />
              </div>
            </div>
          </div>
          <div className="dropdown-item-padding d-flex">
            <div className="d-flex align-items-center">
              <label
                className="form-check-label"
                htmlFor="enableChartsUnitsOnX"
              >
                Enable Charts Units on X axis
              </label>
              <div className="form-switch form-switch-right-text">
                <input
                  className="form-check-input form-switch-right-text"
                  type="checkbox"
                  checked={enableChartsUnitsOnX}
                  onChange={onEnableChartsUnitsOnXChanged}
                  id="enableChartsUnitsOnX"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Settings;
